import React, { useState, useEffect } from 'react';
import {
  CheckCircle,
  XCircle,
  Download,
  X,
  Eye,
  Loader2,
} from 'lucide-react';
import apiService from '../services/api.service';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';

interface SignatureViewerProps {
  open: boolean;
  onClose: () => void;
  /** Contexte heat (legacy) — sinon fournir eventId (+ wodId). */
  heatId?: string;
  /** Contexte résultat : la signature est une propriété du résultat, pas du
      heat — `crossfit_results.heat_id` peut être NULL (sync pré-LOG-165). */
  eventId?: string;
  wodId?: string;
  heatName?: string;
  /** Si fourni, n'affiche que la signature de cette équipe (contexte
      page Résultats : on ouvre depuis le détail d'un score précis). */
  teamId?: string;
}

interface Signature {
  team_id: string;
  team_name: string;
  lane_number: number;
  has_signature: boolean;
  signature_png: string | null;
  signature_timestamp: string | null;
  signed_by: string | null;
}

interface SignaturesResponse {
  heat_id?: string;
  event_id?: string;
  total_teams: number;
  signed_count: number;
  unsigned_count: number;
  signatures: Signature[];
}

const SignatureViewer: React.FC<SignatureViewerProps> = ({
  open,
  onClose,
  heatId,
  eventId,
  wodId,
  heatName,
  teamId,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<SignaturesResponse | null>(null);
  const [selectedSignature, setSelectedSignature] = useState<Signature | null>(null);

  useEffect(() => {
    if (open && (heatId || eventId)) {
      loadSignatures();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, heatId, eventId, wodId, teamId]);

  const loadSignatures = async () => {
    setLoading(true);
    setError(null);
    try {
      // Endpoints cloud (les signatures arrivent dans crossfit_results via la
      // sync). Route heat si on a un heat, sinon route résultat par
      // (événement, WOD, équipe) — robuste aux heat_id NULL (pré-LOG-165).
      const url = heatId
        ? `/heats/${heatId}/signatures`
        : `/events/${eventId}/signatures?${new URLSearchParams({
            ...(wodId ? { wod_id: wodId } : {}),
            ...(teamId ? { team_id: teamId } : {}),
          }).toString()}`;
      const response = await apiService.get(url);
      const payload: SignaturesResponse = response.data;
      if (teamId) {
        const filtered = payload.signatures.filter((s) => s.team_id === teamId);
        const signed = filtered.filter((s) => s.has_signature).length;
        setData({
          ...payload,
          signatures: filtered,
          total_teams: filtered.length,
          signed_count: signed,
          unsigned_count: filtered.length - signed,
        });
      } else {
        setData(payload);
      }
    } catch (err: any) {
      console.error('Erreur chargement signatures:', err);
      setError(err.response?.data?.detail || 'Impossible de charger les signatures');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadSignature = (signature: Signature) => {
    if (!signature.signature_png) return;

    const link = document.createElement('a');
    link.href = `data:image/png;base64,${signature.signature_png}`;
    link.download = `signature_${signature.team_name.replace(/\s+/g, '_')}_lane${signature.lane_number}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatTimestamp = (timestamp: string | null) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <>
      {/* Main Dialog - Signatures List */}
      <Dialog open={open && !selectedSignature} onOpenChange={(isOpen) => !isOpen && onClose()}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Signatures {heatName ? `- ${heatName}` : ''}</span>
              <button onClick={onClose} className="rounded-sm opacity-70 hover:opacity-100">
                <X className="h-4 w-4" />
              </button>
            </DialogTitle>
          </DialogHeader>

          <div className="py-4">
            {loading && (
              <div className="flex justify-center p-8">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            )}

            {error && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {!loading && !error && data && (
              <>
                {/* Summary */}
                <div className="flex gap-2 mb-4">
                  <Badge variant={data.signed_count === data.total_teams ? 'default' : 'secondary'}>
                    {data.signed_count} / {data.total_teams} équipes signées
                  </Badge>
                  {data.unsigned_count > 0 && (
                    <Badge variant="outline" className="text-warning border-warning/40">
                      {data.unsigned_count} non signées
                    </Badge>
                  )}
                </div>

                {/* Teams List */}
                <div className="space-y-2">
                  {data.signatures.map((signature) => (
                    <div
                      key={signature.team_id}
                      className={`flex items-center justify-between p-3 rounded border ${
                        signature.has_signature ? 'bg-success/10 border-success/30' : 'bg-card'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        {signature.has_signature ? (
                          <CheckCircle className="h-5 w-5 text-success" />
                        ) : (
                          <XCircle className="h-5 w-5 text-destructive" />
                        )}
                        <div>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-xs">
                              Lane {signature.lane_number}
                            </Badge>
                            <span className="font-semibold">{signature.team_name}</span>
                          </div>
                          {signature.has_signature ? (
                            <div className="text-xs text-muted-foreground mt-1">
                              Signé le {formatTimestamp(signature.signature_timestamp)}
                              {signature.signed_by && ` par ${signature.signed_by}`}
                            </div>
                          ) : (
                            <div className="text-xs text-muted-foreground mt-1">Non signé</div>
                          )}
                        </div>
                      </div>

                      {signature.has_signature && (
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setSelectedSignature(signature)}
                            title="Voir la signature"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDownloadSignature(signature)}
                            title="Télécharger"
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {data.signatures.length === 0 && (
                  <Alert>
                    <AlertDescription>Aucune équipe assignée à ce heat.</AlertDescription>
                  </Alert>
                )}
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={onClose}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={!!selectedSignature} onOpenChange={(isOpen) => !isOpen && setSelectedSignature(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Signature - {selectedSignature?.team_name}</span>
              <button onClick={() => setSelectedSignature(null)} className="rounded-sm opacity-70 hover:opacity-100">
                <X className="h-4 w-4" />
              </button>
            </DialogTitle>
          </DialogHeader>

          {selectedSignature && (
            <div className="py-4">
              <div className="mb-4 space-y-1 text-sm text-muted-foreground">
                <p><strong>Lane:</strong> {selectedSignature.lane_number}</p>
                <p><strong>Signé le:</strong> {formatTimestamp(selectedSignature.signature_timestamp)}</p>
                {selectedSignature.signed_by && (
                  <p><strong>Par:</strong> {selectedSignature.signed_by}</p>
                )}
              </div>

              <div className="p-4 bg-muted rounded flex justify-center">
                {selectedSignature.signature_png && (
                  <img
                    src={`data:image/png;base64,${selectedSignature.signature_png}`}
                    alt={`Signature ${selectedSignature.team_name}`}
                    className="max-w-full h-auto border rounded"
                  />
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => selectedSignature && handleDownloadSignature(selectedSignature)}
            >
              <Download className="h-4 w-4 mr-2" />
              Télécharger
            </Button>
            <Button variant="outline" onClick={() => setSelectedSignature(null)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SignatureViewer;
